import { useMemo, useCallback, useContext } from 'react';
import { UserContext } from '../../../../../providers/UserContext';
import { FieldTypeContext } from '../../../../../providers/FieldTypeContext';
import { useFormTypes } from '../../../../../use/useFormTypes';
import { formStateDescription, FormStateId } from '#types/System.Parameters.types';

export const useHomeFormVersionsLabelResolver = () => {
  const { user, users } = useContext(UserContext);
  const { fieldTypes } = useContext(FieldTypeContext);
  const formTypes = useFormTypes();

  const fieldOptionLabelByFieldId = useMemo(() => {
    const map = new Map<string, Map<string, string>>();

    for (const [fieldId, fieldType] of Object.entries(fieldTypes)) {
      const options = fieldType.props?.options;
      if (!Array.isArray(options)) continue;

      const optionMap = new Map<string, string>();
      for (const opt of options) {
        const optAny = opt as any;
        const v = optAny?.value;
        if (v === null || v === undefined) continue;
        const key = String(v);
        const label = optAny?.label ?? optAny?.display ?? optAny?.name ?? v;
        optionMap.set(key, String(label));
      }

      if (optionMap.size > 0) {
        map.set(fieldId, optionMap);
      }
    }

    return map;
  }, [fieldTypes]);

  const getLabel = useCallback((columnId: string, value: unknown): string => {
    if (columnId.startsWith('field_') && (typeof value === 'string' || typeof value === 'number')) {
      const fieldId = columnId.replace('field_', '');
      const options = fieldOptionLabelByFieldId.get(fieldId);
      if (options) {
        return options.get(String(value)) ?? '';
      }
    }
    if (columnId === 'formType' && typeof value === 'string') {
      const formType = formTypes.data?.find(ft => ft.docId === value);
      return formType?.description?.shortLabel || value;
    }
    if (columnId === 'formState' && typeof value === 'string') {
      const stateLabel = formStateDescription[value as FormStateId]?.shortLabel;
      if (stateLabel) {
        return stateLabel;
      }
      return value;
    }
    if ((columnId === 'assignedTo' || columnId === 'subscribed' || columnId === 'modifiedBy') && typeof value === 'string') {
      const isCurrentUser = value === user?.uid;
      if (isCurrentUser) {
        return 'Me';
      }

      const foundUser = users?.find(u => u.docId === value);
      if (foundUser) {
        const userName = foundUser.name || '';
        const userEmail = foundUser.email || '';

        return userName || userEmail || value;
      }
      return value;
    }
    return '';
  }, [fieldOptionLabelByFieldId, formTypes.data, users, user?.uid]);

  return getLabel;
};


